import { useMemo } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Link } from 'react-router';
import type { TransactionList, CommissionTransaction } from '../types/commission';

interface CommissionTransactionListProps {
  transactionList: TransactionList | undefined;
  isLoading: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/**
 * Accounting-style ledger with three money columns: Money Out, Money In, Running Balance.
 * Transactions are displayed chronologically (ascending) with a running balance
 * computed from the starting balance.
 */
export function CommissionTransactionList({ transactionList, isLoading }: CommissionTransactionListProps) {
  // Compute running balance per row (hook must be called unconditionally)
  const rows = useMemo(() => {
    if (!transactionList || transactionList.transactions.length === 0) return [];
    const result: (CommissionTransaction & { runningBalance: number })[] = [];
    let balance = transactionList.startingBalance;
    for (const txn of transactionList.transactions) {
      balance += txn.amount;
      result.push({ ...txn, runningBalance: balance });
    }
    return result;
  }, [transactionList]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner label="Loading transactions..." />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
        No commission activity found for this period
      </div>
    );
  }

  return (
    <div className="max-h-[65vh] overflow-auto rounded-md border">
      <table className="w-full caption-bottom text-sm">
      <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0_theme(colors.border)]">
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Ref #</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="text-right">Money Out</TableHead>
          <TableHead className="text-right">Money In</TableHead>
          <TableHead className="text-right">Running Balance</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((txn) => (
          <TableRow key={txn.id}>
            <TableCell>{formatDate(txn.transactionDate)}</TableCell>
            <TableCell>
              {txn.isDraw ? (
                txn.referenceNumber ?? '-'
              ) : txn.jobNumber ? (
                <Link
                  to={`/jobs/${txn.jobId}`}
                  className="text-blue-600 hover:underline"
                >
                  {txn.jobNumber}
                </Link>
              ) : (
                txn.referenceNumber ?? '-'
              )}
            </TableCell>
            <TableCell>{txn.description}</TableCell>
            <TableCell className="text-right tabular-nums">
              {txn.isDraw ? formatCurrency(Math.abs(txn.amount)) : ''}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {!txn.isDraw ? formatCurrency(txn.amount) : ''}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatCurrency(txn.runningBalance)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      </table>
    </div>
  );
}
